import { ColumnDef, flexRender, Row, Table } from '@tanstack/react-table';
import { ActiveSourcingChannelDetails } from 'api-clients/monolith';
import clsx from 'clsx';
import _range from 'lodash/range';
import _startCase from 'lodash/startCase';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { PropsWithChildren, useState } from 'react';
import { useIntl } from 'react-intl';

import EmptyCampaigns from '@/assets/icons/empty-campaigns.svg';
import UserCheck from '@/assets/icons/user-check.svg';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Tag } from '@/components/ui/tag';

import { ChannelRecommendation } from './channel-recommendation';
import { ReachChannels } from './reach-channels';

interface CardsProps {
  table: Table<ActiveSourcingChannelDetails>;
  columns: ColumnDef<ActiveSourcingChannelDetails>[];
  isLoading: boolean;
}

export function Cards({ table, isLoading, columns }: CardsProps) {
  const gridTemplateColumns = `repeat(${
    columns.length - 1
  }, minmax(0, 1fr)) 120px`;

  if (isLoading)
    return (
      <CardsSkeleton
        gridTemplateColumns={gridTemplateColumns}
        columnsLength={columns.length}
      />
    );

  if (table.getRowModel().rows?.length <= 0) return <CardsEmpty />;

  return (
    <>
      {table.getRowModel().rows.map(row => (
        <CardContent
          key={row.id}
          row={row}
          gridTemplateColumns={gridTemplateColumns}
        />
      ))}
    </>
  );
}

function CardContent({
  row,
  gridTemplateColumns,
}: {
  row: Row<ActiveSourcingChannelDetails>;
  gridTemplateColumns: string;
}) {
  const intl = useIntl();
  const [isReachExpanded, setIsReachExpanded] = useState(false);

  const handleExpandReachClick = () => {
    setIsReachExpanded(!isReachExpanded);
  };

  return (
    <div className={clsx('relative', row.original.managed_by && 'mt-3')}>
      {row.original.managed_by && (
        <Tag
          icon={UserCheck}
          text={_startCase(row.original.managed_by)}
          className="bg-fx-teal-50 absolute -top-3 left-3"
        />
      )}
      <RowCard>
        <div className="grid items-start" style={{ gridTemplateColumns }}>
          {row.getVisibleCells().map(cell => (
            <div
              key={cell.id}
              className={clsx(
                'flex flex-col items-start gap-2 py-6 px-5',
                cell.column.id === 'edit' && 'place-self-center',
              )}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}

              {/* Show button to expand reach channels only when the channel is Reach and column is the channel name */}
              {cell.column.id === 'sourcing_channel' &&
                row.original.sourcing_channel.slug === 'reach' && (
                  <Button
                    variant="link"
                    size="sm"
                    className="ml-8 hover:no-underline"
                    onClick={handleExpandReachClick}
                  >
                    {intl.formatMessage({ defaultMessage: 'Channels' })}
                    {isReachExpanded ? <ChevronUp /> : <ChevronDown />}
                  </Button>
                )}
            </div>
          ))}
        </div>
        {isReachExpanded &&
          row.original.sourcing_purchase_id &&
          row.original.sourcing_channel.slug === 'reach' && (
            <ReachChannels
              sourcingPurchaseId={row.original.sourcing_purchase_id}
              gridTemplateColumns={gridTemplateColumns}
            />
          )}
        <ChannelRecommendation channel={row.original} />
      </RowCard>
    </div>
  );
}

function RowCard({ children }: PropsWithChildren) {
  return (
    <div
      className={`hover:bg-fx-neutral-50 border-fx-neutral-200 min-h-30 flex flex-col justify-center rounded-lg border bg-white shadow-sm
    transition-colors`}
    >
      {children}
    </div>
  );
}

function CardsEmpty() {
  const intl = useIntl();

  return (
    <div className="border-fx-neutral-200 rounded-lg border bg-white pb-6 text-center">
      <EmptyState
        icon={EmptyCampaigns}
        header={intl.formatMessage({
          defaultMessage: 'No campaigns to show',
        })}
        description={intl.formatMessage({
          defaultMessage:
            'There are no campaigns for this opening to show at the moment',
        })}
      />
    </div>
  );
}

function CardsSkeleton({
  gridTemplateColumns,
  columnsLength,
}: {
  gridTemplateColumns: string;
  columnsLength: number;
}) {
  return (
    <>
      {_range(2).map(rowIndex => (
        <RowCard key={rowIndex}>
          <div className="grid items-center" style={{ gridTemplateColumns }}>
            <div className="flex h-28 flex-col items-start gap-2 py-6 px-5">
              <div className="flex items-center gap-2">
                <Skeleton className="h-6 w-6" />
                <Skeleton className="h-5 w-24" />
              </div>
            </div>
            {_range(columnsLength - 2).map(colIndex => (
              <div
                key={colIndex}
                className="flex h-28 flex-col items-start gap-3 py-6 px-5"
              >
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-1.5 w-full" />
              </div>
            ))}
            <div className="h-30 flex flex-col items-center justify-between gap-2 py-6 px-5">
              <Skeleton className="h-4 w-8" />
              <Skeleton className="h-6 w-6" />
            </div>
          </div>
        </RowCard>
      ))}
    </>
  );
}
